import React from "react";
import { FiArrowDown, FiArrowRight, FiSearch, FiTrendingUp } from "react-icons/fi";
import { LuHandshake } from "react-icons/lu";
import { GetStartedButton } from "../../components/common/GetStartedButton";
import PlayerView1 from "../../assets/images/PlayerView1.jpeg";
import PlayerView2 from "../../assets/images/PlayerView2.jpeg";

const SCREENS = [
  { title: "All your information in one place.", image: PlayerView1, alt: "JUCO Pipeline player highlight video screen" },
  { title: "Get Recruited", image: PlayerView2, alt: "JUCO Pipeline player profile and stats screen" },
];

const STORY = [
  {
    label: "The Problem",
    heading: "Good players get overlooked.",
    text: "Not every player has access to the same recruiting connections. If you're relying solely on your coach's network, your next opportunity could be limited by who they know.",
  },
  {
    label: "The Solution",
    heading: "Make your performance easier to find.",
    text: "JUCO Pipeline puts player statistics, advanced metrics, and video in one place, giving four-year coaches another way to discover talent beyond their existing recruiting networks.",
  },
];

const CLAIM_STEPS = [
  { title: "Find Your Profile", text: "Locate your existing player profile on JUCO Pipeline." },
  {
    title: "Claim Your Profile",
    text: "Take ownership of your profile and add or update available information, including video and recruiting details.",
  },
  {
    title: "Showcase Your Game",
    text: "Share your profile with coaches and give them another way to evaluate your performance.",
  },
];

const BENEFITS = [
  {
    title: "Get Discovered",
    text: "Reach coaches beyond your program's existing recruiting connections.",
    Icon: FiSearch,
  },
  {
    title: "Prove Your Value",
    text: "Show coaches your production through statistics and performance metrics.",
    Icon: FiTrendingUp,
  },
  {
    title: "Take Control of Your Recruitment",
    text: "Build your profile, share your performance, and take a more active role in finding your next opportunity.",
    Icon: LuHandshake,
  },
];

// Same section label style as the For Coaches page
const EYEBROW = "text-cyan-500 text-sm font-bold uppercase tracking-widest mb-3";

const CARD =
  "rounded-2xl border border-jp-outline/70 bg-jp-card shadow-lg shadow-jp-navy/10 px-6 py-6";

// Points right between cards on wide screens and down when they stack
const FlowArrow = () => (
  <div
    className="flex-shrink-0 self-center w-10 h-10 rounded-full bg-jp-band flex items-center justify-center"
    aria-hidden="true"
  >
    <FiArrowDown className="md:hidden w-5 h-5 text-cyan-400" />
    <FiArrowRight className="hidden md:block w-5 h-5 text-cyan-400" />
  </div>
);

const ForPlayers = () => {
  return (
    <main className="min-h-screen bg-jp-page text-jp-ink text-left overflow-x-hidden">
      {/* Header band: navy in both themes so the white navbar stays readable */}
      <section className="bg-jp-band px-4 sm:px-6 lg:px-8 pt-32 sm:pt-36 pb-14 sm:pb-16">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-8 lg:gap-12 lg:items-end">
          <div>
            <p className="text-yellow-300 text-sm sm:text-base font-bold uppercase tracking-widest mb-4">
              For Players
            </p>
            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-anton leading-[1.1] tracking-tight text-white">
              YOUR GAME DESERVES
              <br />
              <span className="bg-gradient-to-r from-cyan-400 via-blue-400 to-cyan-500 text-transparent bg-clip-text">
                THE NEXT LEVEL
              </span>
            </h1>
          </div>
          <div>
            <p className="text-white text-base sm:text-lg font-semibold leading-relaxed mb-3">
              You’ve put in the hours. The reps. The grind. Now it’s time to get seen.
            </p>
            <p className="text-slate-300 text-sm sm:text-base leading-relaxed mb-6">
              JUCO Pipeline gives you the platform to showcase your talent, tell your story, and connect
              directly with 4-year college coaches who are actively recruiting JUCO athletes.
            </p>
            <GetStartedButton>Get Seen</GetStartedButton>
          </div>
        </div>
      </section>

      {/* Problem and solution */}
      <section className="bg-jp-page px-4 sm:px-6 lg:px-8 pt-16 sm:pt-20">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-12">
          {STORY.map((item) => (
            <div key={item.label}>
              <p className={EYEBROW}>{item.label}</p>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-jp-ink mb-4">{item.heading}</h2>
              <p className="text-jp-muted text-sm sm:text-base leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Benefits and product screens */}
      <section className="bg-jp-page px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6 mb-10 sm:mb-12">
          {BENEFITS.map(({ title, text, Icon }) => (
            <div
              key={title}
              className={`${CARD} flex gap-5`}
            >
              <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-jp-band flex items-center justify-center">
                <Icon className="w-6 h-6 text-cyan-400" aria-hidden="true" />
              </div>
              <div>
                <h3 className="text-jp-ink font-extrabold text-lg sm:text-xl mb-2">{title}</h3>
                <p className="text-jp-muted text-sm sm:text-base leading-relaxed">{text}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-6">
          {SCREENS.map((screen) => (
            <figure key={screen.title} className="m-0">
              <div className="rounded-2xl border border-jp-outline/70 bg-jp-card shadow-lg shadow-jp-navy/10 overflow-hidden">
                <div className="flex items-center gap-1.5 px-4 py-2.5 border-b border-jp-line bg-jp-section">
                  <span className="w-2.5 h-2.5 rounded-full bg-jp-line"></span>
                  <span className="w-2.5 h-2.5 rounded-full bg-jp-line"></span>
                  <span className="w-2.5 h-2.5 rounded-full bg-jp-line"></span>
                </div>
                <div className="aspect-[16/10] bg-[#0B1220]">
                  <img src={screen.image} alt={screen.alt} className="w-full h-full object-contain" />
                </div>
              </div>
              <figcaption className="mt-3 text-jp-ink font-bold uppercase tracking-wider text-sm">
                {screen.title}
              </figcaption>
            </figure>
          ))}
        </div>
      </section>

      {/* How claiming a profile works, flowing into the closing call to action */}
      <section className="bg-jp-page px-4 sm:px-6 lg:px-8 pb-16 sm:pb-20">
        <div className="max-w-7xl mx-auto">
          <p className={EYEBROW}>How Claiming Your Profile Works</p>
          <h2 className="text-2xl sm:text-3xl font-extrabold text-jp-ink mb-8 max-w-3xl">
            Your profile. Your next move.
          </h2>
          <div className="flex flex-col md:flex-row md:items-stretch gap-4">
            {CLAIM_STEPS.map((step, index) => (
              <React.Fragment key={step.title}>
                <div className={`${CARD} md:flex-1`}>
                  <h3 className="text-jp-ink font-extrabold text-lg sm:text-xl mb-2">{step.title}</h3>
                  <p className="text-jp-muted text-sm sm:text-base leading-relaxed">{step.text}</p>
                </div>
                {index < CLAIM_STEPS.length - 1 && <FlowArrow />}
              </React.Fragment>
            ))}
          </div>

          <div className="mt-10 sm:mt-12 rounded-2xl bg-jp-band border border-jp-outline/70 dark:border-jp-gold/40 px-6 py-8 sm:px-10 sm:py-10 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-2">
                You’re not just another number. <span className="text-yellow-300">You’re a prospect.</span>
              </h2>
              <p className="text-slate-300 text-sm sm:text-base">Start proving it.</p>
          </div>
            <div className="flex-shrink-0">
              <GetStartedButton>Get Seen</GetStartedButton>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default ForPlayers;
